// Grand Opening promo helpers — single config served by /api/pricing/config.
// Disable the promotion once in CRM Pricing and the bar, homepage section,
// calculator pricing and quote discounts all switch off together.

// Promo runs on Edmonton (America/Edmonton) calendar dates — mirrors the
// backend exactly: active only from starts_at through expires_at inclusive.
function edmontonToday() {
  // en-CA yields YYYY-MM-DD, so plain string comparison works
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Edmonton" });
}

export function promoActive(promo) {
  if (!promo || !promo.active) return false;
  const today = edmontonToday();
  if (promo.starts_at && today < promo.starts_at) return false;
  if (promo.expires_at && today > promo.expires_at) return false;
  return true;
}

// Cent-precise money: $129 or $99.50 (cents shown only when non-zero)
export function fmtMoney(v) {
  const n = Number(v);
  const hasCents = Math.round(n * 100) % 100 !== 0;
  return `$${n.toLocaleString("en-CA", hasCents
    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : { maximumFractionDigits: 0 })}`;
}

// Must mirror backend: discounted = floor(price * (100 - pct) / 100)
// Whole-dollar pricing — no cents: 50% of $899 = $449, 20% off $199 = $159.
export function promoPrice(price, percent) {
  return Math.floor(price * (100 - percent) / 100);
}

export function promoEligible(promo, serviceKey) {
  return promoActive(promo) && (promo.services || []).includes(serviceKey);
}

// Grand Opening v2: headline % only for the most expensive eligible service in
// the current selection (ties broken by key — mirrors the backend exactly);
// every other eligible service gets the secondary %.
// promo.service_percents overrides both slots (Paint Correction / Ceramic
// Coating at 25% since 2026-10-20 — owner decision; mirrors the backend).
// selected: [{key, price}] — unknown price ranks last.
export function promoServicePcts(promo, selected) {
  const map = {};
  if (!promoActive(promo)) return map;
  const main = Number(promo.percent) || 50;
  const add = Number(promo.multi_percent) || 20;
  const svcPcts = promo.service_percents || {};
  const elig = selected
    .filter((s) => (promo.services || []).includes(s.key))
    .sort((a, b) => (b.price ?? -1) - (a.price ?? -1) || (a.key < b.key ? -1 : 1));
  elig.forEach((s, i) => { map[s.key] = Number(svcPcts[s.key]) || (i === 0 ? main : add); });
  return map;
}

// Display % for a single service viewed on its own (QuickBook cards): the
// per-service override wins, otherwise the headline %.
export function promoPctFor(promo, serviceKey) {
  return Number(promo?.service_percents?.[serviceKey]) || Number(promo?.percent) || 50;
}

// Package price math — always derived from live service/add-on prices, never fixed.
// During Grand Opening the contents get GO pricing (no package % on top — never stacks);
// otherwise the package discount % applies to the package's SERVICES only
// (add-on flags like pc_ceramic_1y are already at a bundled rate — promo_excluded).
// alsoSelected: [{key, price}] of everything already in the cart, so the GO
// 50/20 split ranks bundle contents against the FULL prospective selection,
// exactly like the backend does.
export function bundlePriceInfo(config, bundle, vclass, alsoSelected = []) {
  const promo = config.promo;
  const items = [];
  for (const k of bundle.services || []) {
    const s = (config.services || []).find((x) => x.key === k);
    if (!s) return null;
    let price = vclass ? s.prices?.[vclass] : null;
    if (price == null && s.flat_price != null) price = s.flat_price;
    if (price == null && s.options?.length) price = Math.min(...s.options.map((o) => o.price));
    if (price == null) return null; // custom-priced content
    items.push({ key: k, price });
  }
  for (const fk of bundle.addon_flags || []) {
    const a = config.addons?.[fk];
    const price = a && vclass ? a.prices?.[vclass] : null;
    if (price == null) return null;
    items.push({ key: fk, price, addon: true });
  }
  const regular = Math.round(items.reduce((t, x) => t + x.price, 0) * 100) / 100;
  const services = items.filter((x) => !x.addon);
  const addonSum = items.filter((x) => x.addon).reduce((t, x) => t + x.price, 0);
  if (promoActive(promo)) {
    const merged = [...services];
    for (const s of alsoSelected) if (!merged.some((x) => x.key === s.key)) merged.push(s);
    const pcts = promoServicePcts(promo, merged);
    // Whole-dollar: each service line floors down individually (mirrors backend).
    let final = addonSum;
    for (const s of services) final += Math.floor(s.price * (100 - (pcts[s.key] || 0)) / 100);
    return { regular, final, saved: regular - final, promoApplied: true };
  }
  const pct = Number(bundle.discount_percent) || 10;
  const svcSum = services.reduce((t, x) => t + x.price, 0);
  // Whole-dollar: package price floors down (mirrors backend).
  const saved = svcSum - Math.floor(svcSum * (100 - pct) / 100);
  return { regular, final: regular - saved, saved, promoApplied: false, pct };
}
